import {
  getElementVisual,
  getSignEmoji,
  getSignTrait,
  prettySignLabel,
} from '../ZodiacClashHelpers';
import { useTranslation } from 'react-i18next';

export default function ClashCards({
  styles,
  isMobile,
  visualA,
  visualB,
  justVoted,
  locked,
  voting,
  revealResults,
  winnerChoice,
  handleVote,
  day,
  showPercents,
  displayPctA,
  displayPctB,
}) {
  const { t } = useTranslation('zodiacClash');
  return (
    <>
          <div
            className="zdxCards"
            style={{
              ...styles.cardsContainer,
              flexDirection: isMobile ? 'row' : styles.cardsContainer.flexDirection,
              gap: isMobile ? 10 : styles.cardsContainer.gap,
              alignItems: isMobile ? 'stretch' : styles.cardsContainer.alignItems,
            }}
          >
            <button
              type="button"
              className="zdxCard"
              style={{
                ...styles.card,
                background: visualA.cardBg,
                borderColor: visualA.border,
                transform: justVoted === 'A' ? 'scale(1.02)' : undefined,
                boxShadow: justVoted === 'A'
                  ? `${visualA.glow}, 0 0 40px rgba(255,215,0,0.38)`
                  : `${styles.card.boxShadow}, ${visualA.glow}`,
                opacity: locked && justVoted !== 'A' ? 0.35 : 1,
                cursor: locked || voting ? 'not-allowed' : 'pointer',
                filter: revealResults && winnerChoice && winnerChoice !== 'A' ? 'saturate(0.72) brightness(0.86)' : 'none',
                animation: justVoted === 'A' ? 'clashCardSelected 240ms ease-out both' : styles.card.animation,
              }}
              onClick={() => !locked && !voting && handleVote('A')}
              disabled={locked || voting}
              aria-pressed={day.picked === 'A'}
              aria-label={t('chooseSign', { sign: prettySignLabel(day.signA) })}
            >
              <div className="zdxCardInner" style={styles.cardInner}>
                <div
                  style={{
                    ...styles.cardAura,
                    background: `radial-gradient(circle at center, ${visualA.ring || visualA.aura}, transparent 68%)`,
                  }}
                  aria-hidden="true"
                />
                <div style={{ ...styles.signWatermark, color: visualA.aura }} aria-hidden="true">
                  {getSignEmoji(day.signA)}
                </div>
                <div
                  style={{
                    ...styles.elementPill,
                    borderColor: visualA.border,
                    color: visualA.accent,
                    background: `linear-gradient(135deg, ${visualA.accentSoft || 'rgba(255,255,255,0.08)'}, rgba(0,0,0,0.26))`,
                  }}
                >
                  {getElementVisual(day.signA).label} · {getSignTrait(day.signA)}
                </div>
                <div className="zdxSignName" style={styles.signName}>
                  {prettySignLabel(day.signA)}
                </div>
                <div style={styles.signEmoji} aria-hidden="true">{getSignEmoji(day.signA)}</div>
                <div
                  style={{
                    ...styles.percentBarContainer,
                    opacity: showPercents ? 1 : 0.55,
                    filter: showPercents ? 'none' : 'saturate(0.6)',
                  }}
                  aria-hidden="true"
                >
                  <div
                    style={{
                      ...styles.percentBar,
                      width: showPercents ? `${displayPctA}%` : '100%',
                      background: showPercents
                        ? visualA.accent
                        : 'linear-gradient(90deg, rgba(255,215,0,0.10), rgba(138,43,226,0.18), rgba(255,215,0,0.10))',
                    }}
                  />
                </div>
                <div className={showPercents ? 'zdxCountReveal' : ''} style={styles.percentText}>
                  {showPercents ? `${displayPctA}%` : null}
                </div>
                {showPercents && winnerChoice === 'A' && (
                  <div className="zdxWinnerBurst" aria-hidden="true" />
                )}
              </div>
            </button>

            <div
              className="zdxVsWrapper"
              style={{
                ...styles.vsWrapper,
                marginTop: isMobile ? 8 : styles.vsWrapper.marginTop,
                marginBottom: isMobile ? 8 : styles.vsWrapper.marginBottom,
                gap: isMobile ? 8 : styles.vsWrapper.gap,
              }}
            >
              <div className="zdxVsLine" style={{ ...styles.vsLine, width: isMobile ? 46 : styles.vsLine.width }} />
              <div className="zdxVsText" style={styles.vsText}>⚔</div>
              <div className="zdxVsLine" style={{ ...styles.vsLine, width: isMobile ? 46 : styles.vsLine.width }} />
            </div>

            <button
              type="button"
              className="zdxCard"
              style={{
                ...styles.card,
                background: visualB.cardBg,
                borderColor: visualB.border,
                transform: justVoted === 'B' ? 'scale(1.02)' : undefined,
                boxShadow: justVoted === 'B'
                  ? `${visualB.glow}, 0 0 40px rgba(138,43,226,0.34)`
                  : `${styles.card.boxShadow}, ${visualB.glow}`,
                opacity: locked && justVoted !== 'B' ? 0.35 : 1,
                cursor: locked || voting ? 'not-allowed' : 'pointer',
                filter: revealResults && winnerChoice && winnerChoice !== 'B' ? 'saturate(0.72) brightness(0.86)' : 'none',
                animation: justVoted === 'B' ? 'clashCardSelected 240ms ease-out both' : styles.card.animation,
              }}
              onClick={() => !locked && !voting && handleVote('B')}
              disabled={locked || voting}
              aria-pressed={day.picked === 'B'}
              aria-label={t('chooseSign', { sign: prettySignLabel(day.signB) })}
            >
              <div className="zdxCardInner" style={styles.cardInner}>
                <div
                  style={{
                    ...styles.cardAura,
                    background: `radial-gradient(circle at center, ${visualB.ring || visualB.aura}, transparent 68%)`,
                  }}
                  aria-hidden="true"
                />
                <div style={{ ...styles.signWatermark, color: visualB.aura }} aria-hidden="true">
                  {getSignEmoji(day.signB)}
                </div>
                <div
                  style={{
                    ...styles.elementPill,
                    borderColor: visualB.border,
                    color: visualB.accent,
                    background: `linear-gradient(135deg, ${visualB.accentSoft || 'rgba(255,255,255,0.08)'}, rgba(0,0,0,0.26))`,
                  }}
                >
                  {getElementVisual(day.signB).label} · {getSignTrait(day.signB)}
                </div>
                <div className="zdxSignName" style={styles.signName}>
                  {prettySignLabel(day.signB)}
                </div>
                <div style={styles.signEmoji} aria-hidden="true">{getSignEmoji(day.signB)}</div>
                <div
                  style={{
                    ...styles.percentBarContainer,
                    opacity: showPercents ? 1 : 0.55,
                    filter: showPercents ? 'none' : 'saturate(0.6)',
                  }}
                  aria-hidden="true"
                >
                  <div
                    style={{
                      ...styles.percentBar,
                      width: showPercents ? `${displayPctB}%` : '100%',
                      background: showPercents
                        ? visualB.accent
                        : 'linear-gradient(90deg, rgba(138,43,226,0.12), rgba(255,215,0,0.16), rgba(138,43,226,0.12))',
                    }}
                  />
                </div>
                <div className={showPercents ? 'zdxCountReveal' : ''} style={styles.percentText}>
                  {showPercents ? `${displayPctB}%` : null}
                </div>
                {showPercents && winnerChoice === 'B' && (
                  <div className="zdxWinnerBurst" aria-hidden="true" />
                )}
              </div>
            </button>
          </div>
    </>
  );
}
